/**
 * AppStoreBadges — the App Store + Google Play download pair.
 * ---------------------------------------------------------------------------
 * One component owns the store URLs and the badge artwork so the homepage
 * app strip, the /clients page and the inquiry modal all render the same
 * pair. Both badges are the official black artwork (Apple's SVG, Google's
 * PNG) and are sized so their VISIBLE frames share one height: Apple's SVG
 * has no padding, Google's PNG carries a transparent 41px margin on every
 * side of a 564×168 frame inside a 646×250 canvas, so it is rendered
 * oversize inside a clipped box that shows exactly the frame.
 */

import { cn } from '@/lib/utils';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/luxrally-travel/id6797942626';
export const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.luxrally.luxrally';

// Google badge geometry: canvas 646×250, visible frame 564×168 at +41,+41.
const GOOGLE_CANVAS_H = 250;
const GOOGLE_FRAME_H = 168;
const GOOGLE_FRAME_W = 564;
const GOOGLE_PAD = 41;

interface AppStoreBadgesProps {
    /** Visible badge height in px (both frames render at this height). */
    height?: number;
    className?: string;
}

export function AppStoreBadges({ height = 40, className }: AppStoreBadgesProps) {
    const scale = height / GOOGLE_FRAME_H;
    const googleImgHeight = GOOGLE_CANVAS_H * scale;
    const googlePad = GOOGLE_PAD * scale;
    const googleFrameWidth = GOOGLE_FRAME_W * scale;

    return (
        <div className={cn('flex items-center gap-3', className)} data-testid="app-store-badges">
            <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download LuxRally Travel on the App Store"
                className="block shrink-0 transition-opacity duration-200 hover:opacity-85 focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 focus-visible:outline-none"
            >
                <img src="/app-store-badge.svg" alt="Download on the App Store" width={120} height={40} style={{ height, width: 'auto' }} />
            </a>
            <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get LuxRally Travel on Google Play"
                className="block shrink-0 overflow-hidden transition-opacity duration-200 hover:opacity-85 focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 focus-visible:outline-none"
                style={{ height, width: googleFrameWidth }}
            >
                <img
                    src="/google-play-badge.png"
                    alt="Get it on Google Play"
                    width={646}
                    height={250}
                    className="max-w-none"
                    style={{ height: googleImgHeight, width: 'auto', margin: -googlePad }}
                />
            </a>
        </div>
    );
}
